import { DomUtil, Bounds } from 'leaflet';
import Layer from './Layer';

/**
 * 画布图层基类
 * @interface
 */
export default class CanvasLayer extends Layer {
    /**
     * 构造函数
     * @param {string} id 图层ID
     * @param {Object} options 可选项
     */
    constructor(id, options) {
        super(id, {
            selectable: true, // 数据是否可被选择
            visiable: true, // 图层是否可见
            zoomAnimation: true,
            ...options
        });
    }

    /**
     * 图层添加到地图时调用
     * @param map
     */
    onAdd(map) {
        super.onAdd(map);

        if (!this._container) {
            this._initContainer(); // defined by renderer implementations
        }

        this.getPane().appendChild(this._container);

        this._map.on('moveend', this.redraw, this);
        this._map.on('resize', this.resize, this);

        this.redraw();
    }

    /**
     * 图层被移除时调用
     * @param map
     */
    onRemove(map) {
        this._map.off('moveend', this.redraw, this);
        this._map.off('resize', this.resize, this);

        this.getPane().removeChild(this._container);

        this._destroyContainer();

        super.onRemove(map);
    }

    /**
     * 初始化图层容器
     */
    _initContainer() {
        const container = DomUtil.create('div', 'leaflet-canvas-container');
        container.id = this.id || '';
        container.style.position = 'absolute';
        container.style.width = `${this._map.getSize().x}px`;
        container.style.height = `${this._map.getSize().y}px`;

        const canvas = document.createElement('canvas');
        canvas.width = this._map.getSize().x;
        canvas.height = this._map.getSize().y;
        container.appendChild(canvas);

        if (this._zoomAnimated && this.options.zoomAnimation) {
            DomUtil.addClass(container, 'leaflet-zoom-animated');
        }

        this._container = container;
        this._canvas = canvas;
        this._ctx = canvas.getContext('2d');

        this._updateZIndex();
    }

    /**
     * 销毁html容器
     */
    _destroyContainer() {
        const parent = this._container.parentNode;
        if (parent) {
            parent.removeChild(this._container);
        }

        delete this._ctx;
        delete this._canvas;
        delete this._container;
    }

    /**
     * 重置容器大小
     */
    resize() {
        this._container.style.width = `${this._map.getSize().x}px`;
        this._container.style.height = `${this._map.getSize().y}px`;
        this._canvas.width = this._map.getSize().x;
        this._canvas.height = this._map.getSize().y;

        this._bounds = this._map.getBounds();
    }

    /**
     * 设置页面层级
     * @param {number} zIndex 页面层级
     */
    setZIndex(zIndex) {
        this.options.zIndex = zIndex;
        this._updateZIndex();
        return this;
    }

    /**
     * 将图层的页面层级调整为最高层级
     */
    bringToFront() {
        if (this._map) {
            this._toFront(this._container);
            this._setAutoZIndex(Math.max);
        }
        return this;
    }

    /**
     * 将图层的页面层级调整为最低层级
     */
    bringToBack() {
        if (this._map) {
            this._toBack(this._container);
            this._setAutoZIndex(Math.min);
        }
        return this;
    }

    /**
     * 自动调整图层页面层级
     * @param {function} compare 比较函数
     */
    _setAutoZIndex(compare) {
        // go through all other layers of the same pane, set zIndex to max + 1 (front) or min - 1 (back)

        const layers = this.getPane().children;
        let edgeZIndex = -compare(-Infinity, Infinity); // -Infinity for max, Infinity for min

        for (let i = 0, len = layers.length, zIndex; i < len; i++) {
            zIndex = layers[i].style.zIndex;

            if (layers[i] !== this._container && zIndex) {
                edgeZIndex = compare(edgeZIndex, +zIndex);
            }
        }

        if (window.isFinite(edgeZIndex)) {
            this.options.zIndex = edgeZIndex + compare(-1, 1);
            this._updateZIndex();
        }
    }

    /**
     * 更新页面层级
     */
    _updateZIndex() {
        if (this._container && this.options.zIndex !== undefined && this.options.zIndex !== null) {
            this._container.style.zIndex = this.options.zIndex;
        }
    }

    /**
     * 重绘图层
     */
    redraw() {
        if (!this._map || this._map._animatingZoom) {
            return;
        }

        this._bounds = this._map.getBounds();
        this._zoom = this._map.getZoom();

        this._clear();

        this._resetPosition();

        if (this._canDraw()) {
            this._draw();
        }
    }

    /**
     * 清空图层
     */
    _clear() {
        this._ctx.clearRect(0, 0, this._canvas.width, this._canvas.height);
        // 解决canvas内容清空不了的问题
        this._canvas.width = this._canvas.width;
    }

    /**
     * 重新调整图层位置
     */
    _resetPosition() {
        const bounds = this._map.getBounds();
        const topLeft = this._map.latLngToLayerPoint(bounds.getNorthWest());
        DomUtil.setPosition(this._container, topLeft);
    }

    /**
     * 绘制图层
     */
    _draw() {}

    /**
     * 判断图层是否可以绘制
     */
    _canDraw() {
        if (this.options.visiable === false) {
            // 排除undefined/null/0/''的情况
            return false;
        }

        const zoom = this._map.getZoom();
        const minZoom = this.options.minZoom || this._map.getMinZoom();
        const maxZoom = this.options.maxZoom || this._map.getMaxZoom();
        if (zoom < minZoom || zoom > maxZoom) {
            return false;
        }

        return true;
    }

    /**
     * 设置图层是否可见，并返回可见状态
     * @param {boolean} value 是否可见
     */
    visiable(value) {
        if (typeof value === 'boolean') {
            this.options.visiable = value;
            this.redraw();
        }

        return this.options.visiable;
    }

    /**
     * 设置图层的渲染要素是否可以被选择，并返回可选择状态
     * @param {boolean} value
     */
    selectable(value) {
        if (typeof value === 'boolean') {
            this.options.selectable = value;
        }

        return this.options.selectable;
    }

    // 重写父类的方法
    getEvents() {
        const events = {
        };

        // 支持图层级的缩放动画设置
        if (this._zoomAnimated && this.options.zoomAnimation) {
            events.zoomanim = this._zoomanim;
            events.zoom = this._onZoom;
        }
        return events;
    }

    _onZoom() {
        this._animateZoom(this._map.getZoom(), this._map.getCenter());
    }

    _zoomanim(event) {
        this._animateZoom(event.zoom, event.center);
    }

    _animateZoom(zoom, center) {
        const scale = this._map.getZoomScale(zoom, this._zoom);
        const offset = this._map._latLngBoundsToNewLayerBounds(
            this._bounds,
            zoom,
            center
        ).min;

        DomUtil.setTransform(this._container, offset, scale);
    }
}
